import { cloneDeep } from '../../utils/utils';
import { NavigationLocked } from '../../components/slides/constants/navigationOptions';

/**
 * isSomePreviousLocked: returns if there is some locked slide in navigationMap while building (backwards slides)
 * @param navigationMap
 * @param slideArray
 * @returns {boolean}
 */
function isSomePreviousLocked(navigationMap, slideArray) {
	let previousLocked = false;
	for (const slide of navigationMap) {
		const slideContent = slideArray.find(slideA => slideA.id === slide.id);

		const lockedAndNotCompleted = !slide.available && !slide.completion;
		const slideSingleLockedAndNotCompleted = !slideContent.children && slideContent.locked && !slide.completion;
		if (lockedAndNotCompleted || slideSingleLockedAndNotCompleted) {
			previousLocked = true;
			break;
		}
	}
	return previousLocked;
}

/**
 * isAvailable: returns if current slide is available to be visualized by user
 * @param previous previous slide
 * @param globalLocked current slide is locked by "general lock"
 * @param currentSlideLocked current slide is locked by "slide individual lock"
 * @param activityState slide's state array
 * @param navigationMap navigation state map
 * @param slide current slide
 * @param slideArray
 * @returns {*|boolean}
 */
export function isAvailable(previous, globalLocked, currentSlideLocked, activityState, navigationMap, slide, slideArray) {
	if (!previous) return true; // slide is the first one
	const previousLocked = isSomePreviousLocked(navigationMap, slideArray);
	if (previous.type === 'ResultsSlideTemplate') {
		//Special case
		return getSlideAvailability(previous, navigationMap);
	} else if (slide.type !== 'ResultsSlideTemplate' && getSlideCompletion(slide, activityState)) {
		// Adaptive Learning exception: avoid to set slide as lock if Adaptive Learning has set slide as completed
		return true;
	} else if (previous.children) {
		// Current is the first element of section/quiz (it can be slide/section)
		if (currentSlideLocked) {
			return getSlideAvailability(previous, navigationMap);
		}
		return previousLocked ? false : getSlideAvailability(previous, navigationMap);
	} else {
		// Current is not the first element of section/quiz
		if (globalLocked) {
			return getSlideAvailability(previous, navigationMap) && getSlideCompletion(previous, activityState);
		} else if (currentSlideLocked) {
			return previousLocked ? false : getSlideAvailability(previous, navigationMap);
		} else {
			return !previousLocked;
		}
	}
}

/**
 * getSlideAvailability: returns available prop of previous slide passed as argument
 * @param previous
 * @param navigationMap
 * @returns {boolean|*}
 */
function getSlideAvailability(previous, navigationMap) {
	const previousState = navigationMap.find(slide => slide.id === previous.id);
	return previousState ? previousState.available : false;
}

/**
 * getSlideCompletion: returns completion prop of slide passed as argument
 * @param slide
 * @param activityState
 * @returns {*|undefined}
 */
export function getSlideCompletion(slide, activityState) {
	if (slide.type === 'ResultsSlideTemplate') {
		return true;
	}
	if (slide.path.length < 2) {
		return activityState ? activityState.activities[slide.id].completion : undefined;
	} else {
		let completionSlide = activityState;
		slide.path.forEach(path => {
			completionSlide = completionSlide.activities[path.id];
		});
		return completionSlide ? completionSlide.completion : undefined;
	}
}

/**
 * Set prop locked to each section's children
 * @param children
 * @param type
 */
export function setChildrenLocked(children, type) {
	children.forEach(child => {
		child.locked = type;
		if (child.children && child.children.length > 0) {
			setChildrenLocked(child.children, type);
		}
	});
}

export function addPathToItem(item, parent) {
	let path = []
	const itemPath = { id: item.id, title: item.title, hidden: item.skip }

	if (parent) {
		path = cloneDeep(parent.path)
		if (path) {
			path.push(itemPath)
		}
	} else {
		path = [itemPath]
	}
	item.path = path
}

export function buildNavigationMap(slides, activityState, interactivePresentationLocked, applyAvailabilityRules) {
	if (!applyAvailabilityRules) {
		return slides.map(slide => ({
			id: slide.id,
			completion: false,
			available: true
		}));
	}

	const navigationMap = [];
	let previousSlide = false;

	slides.forEach(slide => {
		if (slide.locked === true) {
			slide.locked = NavigationLocked.SEQUENTIAL.value;
		}
		const currentSlideLocked = slide.locked ? !getSlideCompletion(slide, activityState) : false;
		const currentSectionLocked = slide.locked && slide.children && slide.children.length > 0;
		if (currentSectionLocked) {
			setChildrenLocked(slide.children, slide.locked);
		}
		navigationMap.push({
			id: slide.id,
			completion: getSlideCompletion(slide, activityState),
			available: isAvailable(
				previousSlide,
				interactivePresentationLocked,
				currentSlideLocked,
				activityState,
				navigationMap,
				slide,
				slides
			)
		});
		previousSlide = slide;
	});
	return navigationMap;
}
